import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useApiGenerationContext } from "../Context/ApiGenerationContext";
import { useEngineConfigContext } from "../Context/EngineConfigContext";
import DashboardLayout from "../Layout/DashboardLayout";
import DashboardTopbar from "../Layout/DashboardTopbar";
import useTokenBalance from "../hooks/useTokenBalance";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../Context/AuthContext";
import { useAlertContext } from "../Context/AlertContext";
import { useMyApiContext } from "../Context/MyApiContext";
import { consumeTokenLot, USE_LOT_ERROR_CODES } from "../utils/useLotApi";
import QueryBuilder from "react-querybuilder";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { materialControlElements, QueryBuilderMaterial } from "@react-querybuilder/material";
import { fieldOperatorsByType } from "../Context/FieldProperties";
import "./ApiGenerator/api-generator.css";
import {
  clearApiGeneratorPersistence,
  getPersistedWizardUiState,
  saveApiGeneratorPersistence
} from "../utils/apiGeneratorPersistence";

const muiTheme = createTheme();
const persistedUi = getPersistedWizardUiState();

const METHOD_HTTP = {
  view: "GET",
  create: "POST",
  update: "PUT",
  delete: "DELETE"
};

const ApiGeneration = () => {
  const { list: engineConfigs, currentConfig } = useEngineConfigContext();
  const {
    fetchUserAttributes,
    userAttributes: { length: userAttrLength }
    // refreshSession
  } = useAuthContext();
  const {
    refreshSession,
    models,
    fetchModels,
    fetching,
    currentModel,
    setCurrentModel,
    fetchFields,
    fields,
    modelRecords,
    fetchModelRecords,
    fetchRecords,
    selectedRecord,
    setSelectedRecord,
    selectedFields,
    setSelectedFields,
    filterFields,
    setFilterFields,
    requiredFields,
    setRequiredFields,
    fieldType,
    setFieldType,
    selectedMethod,
    setSelectedMethod,
    selectedConfig,
    setSelectedConfig,
    selectedVersion,
    setModelRecords,
    completeQuery,
    setCompleteQuery,
    QBFields,
    setQBFields
  } = useApiGenerationContext();
  const { save, saving } = useMyApiContext();
  const { setError } = useAlertContext();
  const { tokenBalance } = useTokenBalance();
  const { t } = useTranslation("common");
  const [saveApi, setSaveApi] = useState(persistedUi.saveApi);
  const [fileName, setFileName] = useState(persistedUi.fileName);
  const [fieldNameFilter, setFieldNameFilter] = useState(persistedUi.fieldNameFilter);
  const [labelNameFilter, setLableNameFilter] = useState(persistedUi.labelNameFilter);
  const [showingRetryButton, setShowingRetryButton] = useState(false);
  const [selectAll, setSelectAll] = useState(persistedUi.selectAll);
  const [currentStep, setCurrentStep] = useState(persistedUi.currentStep);
  const [viewApiBusy, setViewApiBusy] = useState(false);
  const navigate = useNavigate();
  let searchQuery = [];
  const [query, setQuery] = useState(persistedUi.query);

  const [filteredFields, setFilteredFields] = useState(fields);

  const methodOptions = useMemo(
    () => [
      ["view", t("View")],
      ["create", t("Create")],
      ["update", t("Update")],
      ["delete", t("Delete")]
    ],
    [t]
  );

  const httpMethod = METHOD_HTTP[selectedMethod] || "GET";

  useEffect(() => {
    if (currentConfig?.name && !selectedConfig) {
      setSelectedConfig(currentConfig.name);
    }
  }, [currentConfig, selectedConfig, setSelectedConfig]);

  const handleViewApi = async () => {
    if (viewApiBusy) return;
    setViewApiBusy(true);
    try {
      await consumeTokenLot();
      navigate("/api-generator/view");
    } catch (error) {
      console.error("useLot failed:", error.message);
      if (error.code === USE_LOT_ERROR_CODES.TOKENS_UNAVAILABLE) {
        setError(
          error.message ||
            t("Sorry, tokens not available in lots")
        );
      } else {
        setError(error.message || t("Unable to consume token. Please try again."));
      }
    } finally {
      setViewApiBusy(false);
    }
  };

  const goStep = (step) => {
    setCurrentStep(step);
  };

  useEffect(() => {
    if (!selectedConfig) return;

    saveApiGeneratorPersistence({
      currentStep,
      selectedConfig,
      currentModel,
      selectedMethod,
      selectedRecord,
      selectedFields,
      requiredFields,
      fieldType,
      QBFields,
      completeQuery,
      filterFields,
      query,
      fileName,
      saveApi,
      fieldNameFilter,
      labelNameFilter,
      selectAll,
      selectedVersion,
      models,
      prevModel: currentModel,
      prevConfig: selectedConfig
    });
  }, [
    currentStep,
    selectedConfig,
    currentModel,
    selectedMethod,
    selectedRecord,
    selectedFields,
    requiredFields,
    fieldType,
    QBFields,
    completeQuery,
    filterFields,
    query,
    fileName,
    saveApi,
    fieldNameFilter,
    labelNameFilter,
    selectAll,
    selectedVersion,
    models
  ]);

  useEffect(() => {
    setCurrentStep((step) => {
      if (step >= 3 && selectedConfig && currentModel && selectedMethod) return 3;
      if (step >= 2 && selectedConfig) return 2;
      return 1;
    });
  }, [selectedConfig, currentModel, selectedMethod]);

  const removeSelectedField = (field) => {
    if (!selectedFields.includes(field)) return;
    setSelectedFields(selectedFields.filter((f) => f !== field));
    setQBFields(QBFields.filter((f) => f.name !== field));
    if (fieldType[field]) {
      const next = { ...fieldType };
      delete next[field];
      setFieldType(next);
    }
  };

  const clearSelectedFields = () => {
    setSelectedFields(requiredFields.length ? [...requiredFields] : []);
    setQBFields([]);
    setFieldType(
      requiredFields.reduce((acc, name) => {
        if (fields[name]) acc[name] = { type: fields[name].type };
        return acc;
      }, {})
    );
    setSelectAll(false);
  };

  useEffect(() => {
    if (["update", "delete", "view"].includes(selectedMethod)) {
      if (modelRecords.length === 0) {
        fetchModelRecords(currentModel);
      }
    }
    console.log(selectedMethod, selectedFields);
    if (["update", "create"].includes(selectedMethod)) {
      let newfields = selectedFields.filter((elem) => !fields[elem].readonly);
      console.log(newfields);
      setSelectedFields(newfields);
    }
  }, [currentModel, selectedMethod]);
  const handleSelectAll = () => {
    const nonRequiredFields = Object.entries(filteredFields)
      // eslint-disable-next-line no-unused-vars
      .filter(([name, options]) => !options.required)
      .map(([name]) => name);

    if (selectAll) {
      // Deselect all checkboxes
      setSelectedFields(selectedFields.filter((field) => !nonRequiredFields.includes(field)));
    } else {
      // Select all checkboxes
      setSelectedFields([...new Set([...selectedFields, ...nonRequiredFields])]);
    }
    setSelectAll(!selectAll);
  };
  const toggleSelectedFields = (field, type) => {
    if (selectedFields.includes(field)) {
      setSelectedFields(selectedFields.filter((f) => f !== field));
      setQBFields(QBFields.filter((f) => f.name !== field));
    } else {
      setSelectedFields([...selectedFields, field]);
      let f = fields[field];
      let ops = fieldOperatorsByType[f.type]
        ? { operators: fieldOperatorsByType[f.type].operators }
        : {};

      if (
        !f.readOnly &&
        (f.type === "selection" ||
          f.type === "one2one" ||
          f.type === "many2one" ||
          f.type === "one2many" ||
          f.type === "many2many")
      ) {
        fetchRecords(fields[field].relation).then((response) => {
          let options = response.map((obj) => {
            return { label: obj.display_name + "(id:" + obj.id + ")", name: obj.id };
          });
          console.log("response", options, {
            ...ops,
            values: options,
            name: f.name,
            label: f.string,
            // type: "select",
            inputType: "select",
            valueEditorType: "select"
          });
          setQBFields([
            ...QBFields,
            {
              values: options,
              name: f.name,
              label: f.string,
              // type: f.type,
              inputType: "select",
              valueEditorType: "select"
            }
          ]);
        });
      } else {
        setQBFields([
          ...QBFields,
          {
            // ...ops,
            name: f.name,
            label: f.string,
            // type: f.type,
            inputType: f.type == "boolean" ? "checkbox" : f.type == "integer" ? "number" : f.type,
            valueEditorType:
              f.type == "boolean" ? "checkbox" : f.type == "integer" ? "number" : f.type
          }
        ]);
      }
      console.log(fields, QBFields);
    }
    togglefieldType(field, type);
  };

  const togglefieldType = (field, type) => {
    if (fieldType && fieldType[field]) {
      delete fieldType[field];
    } else {
      fieldType[field] = { type };
    }
    setFieldType(fieldType);
  };

  // const toggleSelectedFilterFields = (field) => {
  //   console.log(filterFields, field);
  //   if (filterFields.indexOf(field) > -1) {
  //     setFilterFields(filterFields.filter((f) => f !== field));
  //   } else {
  //     setFilterFields([...filterFields, field]);
  //   }
  // };

  useEffect(() => {
    if (userAttrLength === 0) {
      fetchUserAttributes();
    }
  }, []);

  useEffect(() => {
    setFilteredFields(fields);
  }, [fields]);

  useEffect(() => {
    // console.log(fields);
    let keys = Object.keys(fields).filter((element) => {
      let nameret = true;
      let labelret = true;
      if (labelNameFilter) {
        labelret = fields[element].string.toLowerCase().includes(labelNameFilter.toLowerCase());
      }
      if (fieldNameFilter) {
        nameret = element.toLowerCase().includes(fieldNameFilter.toLowerCase());
      }
      return nameret && labelret;
    });
    let newArr = {};
    keys.map((elem) => (newArr[elem] = fields[elem]));
    setFilteredFields(newArr);
  }, [fieldNameFilter, labelNameFilter]);

  useEffect(() => {
    fetchFields();
  }, [currentModel]);

  useEffect(() => {
    searchQuery = [];
    checkHaveRuleWithin(query);
    setCompleteQuery(searchQuery);
    console.log("completeQuery ", query, JSON.stringify(completeQuery));
  }, [query]);

  useEffect(() => {
    let execute = async () => {
      try {
        await fetchModels();
      } catch (e) {
        // Auto Refresh
        console.log("fetching models failed");
        try {
          if (e?.request?.status === 401 || e?.request?.status === 403) {
            // console.log("refreshing session");
            await refreshSession();
            // console.log("session refreshed");
          }
          console.log("fetching models again");
          await fetchModels();
        } catch (e) {
          console.log("something went wrong on retry", e);
          setShowingRetryButton(true);
        }
      }
    };
    execute();
  }, [selectedConfig]);

  const checkHaveRuleWithin = (q) => {
    if (q && q.rules && q.rules.length > 0 && q.combinator) {
      if (q.rules.length > 1) searchQuery.push(q.combinator == "and" ? "&" : "|");
      q.rules.map((rule) => {
        if (rule && rule.field && rule.operator) {
          searchQuery.push([rule.field, rule.operator, rule.value]);
        }
        if (rule.rules && rule.rules.length > 0) {
          checkHaveRuleWithin(rule);
        }
      });
    }
  };
  const engineOptions =
    useMemo(() => {
      return engineConfigs?.map(({ name }) => [name, name]);
    }, [engineConfigs]) || [];

  const modelOptions = useMemo(() => {
    return models?.map(({ model, display_name }) => [model, display_name]) || [];
  }, [models]);

  const recordsOptions = useMemo(() => {
    return modelRecords?.map(({ id, display_name }) => [id, display_name]) || [];
  }, [modelRecords]);

  const handleSelectConfig = useCallback(
    (confId) => {
      setSelectedConfig(confId);
    },
    [engineConfigs]
  );

  const handleModelChange = useCallback(
    (model) => {
      setCurrentModel(model);
      setFieldType({});
      setModelRecords([]);
      setSelectedRecord(null);
      setSelectedFields([]);
      setSelectedMethod(null);
    },
    [models]
  );

  const handleMethodChange = (method) => {
    setSelectedMethod(method);
  };

  const handleSave = async () => {
    let payload = {
      config: selectedConfig,
      model: currentModel,

      serviceValidation: true,
      required: requiredFields,
      inputFields: selectedFields,
      properties: fieldType,
      outputrecords: filterFields,
      action: selectedMethod,
      apiName: fileName,
      filename: fileName,
      record: selectedRecord,
      fields: completeQuery
    };
    await save(payload);
    clearApiGeneratorPersistence();
    navigate("/my-apis");
  };

  const handleRecordChange = (recordId) => {
    setSelectedRecord(recordId);
    console.log(selectedRecord);
  };

  const clearAll = () => {
    setQuery({ combinator: "and", rules: [] });
  };

  const clearValues = () => {
    setSelectedConfig(null);
    setCurrentModel(null);
    setSelectedRecord(null);
    setSelectedMethod("");
    setSelectedFields([]);
    setRequiredFields([]);
    setFilterFields([]);
    setFieldType({});
    setQBFields([]);
    setQuery({ combinator: "and", rules: [] });
    setCurrentStep(1);
    clearApiGeneratorPersistence();
  };

  const getStepTabClass = (step) => {
    let cls = "step-tab";
    if (currentStep === step) cls += " active";
    else if (step === 1 && selectedConfig) cls += " done";
    else if (step === 2 && currentModel && selectedMethod) cls += " done";
    else if (step === 3 && selectedFields.length > 0) cls += " done";
    return cls;
  };

  const getStepNum = (step) => {
    if (step === 1 && selectedConfig && currentStep !== 1) return "✓";
    if (step === 2 && currentModel && selectedMethod && currentStep > 2) return "✓";
    return step;
  };

  const renderReqBadge = (required) =>
    required ? (
      <span className="req-badge req-yes">{t("Required")}</span>
    ) : (
      <span className="req-badge req-no">—</span>
    );

  const renderRoBadge = (readonly) =>
    readonly ? (
      <span className="ro-badge ro-yes">{t("Read Only")}</span>
    ) : (
      <span className="ro-badge ro-no">—</span>
    );

  return (
    <DashboardLayout>
      <div className="dashboard-page">
        <DashboardTopbar title={t("API Generator")} tokenBalance={tokenBalance}>
          <Link to="/my-apis" className="btn btn-ghost btn-sm">
            {t("My APIs")}
          </Link>
        </DashboardTopbar>

        <div className="page-content">
          <div className="page-header">
            <h1>{t("API Generator")}</h1>
            <p>{t("Build a REST endpoint from any Odoo model")}</p>
          </div>

          <div className="steps-nav">
            <button type="button" className={getStepTabClass(1)} onClick={() => goStep(1)}>
              <span className="step-num">{getStepNum(1)}</span>
              {t("Connection")}
            </button>
            <button
              type="button"
              className={getStepTabClass(2)}
              onClick={() => selectedConfig && goStep(2)}
              disabled={!selectedConfig}>
              <span className="step-num">{getStepNum(2)}</span>
              {t("Model & Action")}
            </button>
            <button
              type="button"
              className={getStepTabClass(3)}
              onClick={() => currentModel && selectedMethod && goStep(3)}
              disabled={!currentModel || !selectedMethod}>
              <span className="step-num">{getStepNum(3)}</span>
              {t("Fields")}
            </button>
          </div>

          {/* Step 1 */}
          <div className={`step-panel${currentStep === 1 ? " active" : ""}`}>
            <div className="card step-card">
              <div className="card-header">
                <div>
                  <div className="card-title">
                    <span className="step-number">1</span>
                    {t("Step 1 — Connection & Model")}
                  </div>
                  <div className="card-subtitle">
                    {t("Select which Odoo instance and data model to expose")}
                  </div>
                </div>
                <button type="button" className="btn btn-ghost btn-sm" onClick={clearValues}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                  {t("Clear")}
                </button>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="engineConfig">
                  {t("Connection")}
                </label>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <select
                    id="engineConfig"
                    className="form-control"
                    value={selectedConfig || ""}
                    disabled={fetching}
                    onChange={(e) => handleSelectConfig(e.target.value)}>
                    <option value="">{t("Select")}</option>
                    {engineOptions.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  {showingRetryButton ? (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={fetchModels} disabled={fetching}>
                      {t("Retry")}
                    </button>
                  ) : null}
                  {fetching ? <span className="dashboard-loading" style={{ minHeight: 0, padding: 0 }}>{t("Fetching...")}</span> : null}
                </div>
              </div>

              <div className="step-actions step-actions-end">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!selectedConfig}
                  onClick={() => goStep(2)}>
                  {t("Next →")}
                </button>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className={`step-panel${currentStep === 2 ? " active" : ""}`}>
            {selectedConfig && models.length ? (
              <div className="card step-card">
                <div className="card-header">
                  <div>
                    <div className="card-title">
                      <span className="step-number">2</span>
                      {t("Step 2 — Model, Action & Record")}
                    </div>
                  </div>
                  {selectedMethod ? (
                    <span className={`api-method-badge ${selectedMethod}`}>{httpMethod}</span>
                  ) : null}
                </div>

                <div className="model-action-grid">
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label" htmlFor="model">
                      {t("Model")}
                    </label>
                    <select
                      id="model"
                      className="form-control"
                      value={currentModel || ""}
                      onChange={(e) => handleModelChange(e.target.value)}>
                      <option value="">{t("Select")}</option>
                      {modelOptions.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {currentModel ? (
                    <>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" htmlFor="method">
                          {t("API Action")}
                        </label>
                        <select
                          id="method"
                          className="form-control"
                          value={selectedMethod || ""}
                          onChange={(e) => handleMethodChange(e.target.value)}>
                          <option value="">{t("Select")}</option>
                          {methodOptions.map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {["update", "delete", "view"].includes(selectedMethod) ? (
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label" htmlFor="record">
                            {t("Choose record")}
                          </label>
                          <select
                            id="record"
                            className="form-control"
                            value={selectedRecord || ""}
                            onChange={(e) => handleRecordChange(e.target.value)}>
                            <option value="">— {t("All Records")} —</option>
                            {recordsOptions.map(([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <div />
                      )}
                    </>
                  ) : (
                    <>
                      <div />
                      <div />
                    </>
                  )}
                </div>

                <div className="step-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => goStep(1)}>
                    {t("← Back")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!currentModel || !selectedMethod}
                    onClick={() => goStep(3)}>
                    {t("Next →")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="dashboard-empty">{fetching ? t("Fetching...") : t("Select")}</div>
            )}
          </div>

          {/* Step 3 */}
          <div className={`step-panel${currentStep === 3 ? " active" : ""}`}>
            {currentModel && selectedMethod ? (
              <>
                <div className="card step-card" style={{ marginBottom: 20 }}>
                  <div className="card-header">
                    <div>
                      <div className="card-title">
                        <span className="step-number">3</span>
                        {t("Step 3 — Selected Fields")}
                      </div>
                      <div className="card-subtitle">
                        {t("Check fields below to include them in the API response")}
                      </div>
                    </div>
                    <div className="rule-row-header">
                      <span className={`api-method-badge ${selectedMethod || "view"}`}>{httpMethod}</span>
                      <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                        {t("Selected:")}{" "}
                        <strong style={{ color: "var(--blue-light)" }}>{selectedFields.length}</strong>
                      </span>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={clearSelectedFields}>
                        {t("Clear All")}
                      </button>
                    </div>
                  </div>

                  <div className="field-tags">
                    <span className="field-tags-label">{t("Selected:")}</span>
                    {selectedFields.length ? (
                      selectedFields.map((field) => (
                        <span key={field} className="field-tag">
                          {field}
                          <button type="button" className="rm" onClick={() => removeSelectedField(field)}>
                            ✕
                          </button>
                        </span>
                      ))
                    ) : (
                      <span style={{ color: "var(--text-muted)", fontSize: "0.78rem" }}>{t("No fields selected yet")}</span>
                    )}
                  </div>

                  {currentModel && ["view"].includes(selectedMethod) ? (
                    <div className="generator-query-wrap">
                      <ThemeProvider theme={muiTheme}>
                        <QueryBuilderMaterial>
                          <QueryBuilder
                            fields={QBFields}
                            showCombinatorsBetweenRules
                            showNotToggle
                            query={query}
                            onQueryChange={setQuery}
                            controlElements={materialControlElements}
                          />
                        </QueryBuilderMaterial>
                      </ThemeProvider>
                      <div style={{ marginTop: 10 }}>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={clearAll}>
                          {t("Clear All")}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <div className="step-actions" style={{ marginTop: 16 }}>
                    <button type="button" className="btn btn-ghost" onClick={() => goStep(2)}>
                      {t("← Back")}
                    </button>
                    <button type="button" className="btn btn-primary" onClick={() => setSaveApi(true)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                        <polyline points="17 21 17 13 7 13 7 21" />
                        <polyline points="7 3 7 8 15 8" />
                      </svg>
                      {t("Save Api")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={handleViewApi}
                      disabled={viewApiBusy}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      {viewApiBusy ? t("Fetching...") : t("View Api")}
                    </button>
                  </div>

                  {saveApi ? (
                    <div className="save-filename-panel">
                      <input
                        type="text"
                        className="form-control"
                        value={fileName}
                        id="file_name"
                        name="file_name"
                        placeholder="Enter file name"
                        onChange={(e) => setFileName(e.target.value)}
                      />
                      <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                        {t("Save")}
                      </button>
                      <button type="button" className="btn btn-ghost" onClick={() => setSaveApi(false)}>
                        {t("Close")}
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="card">
                  <div className="card-header">
                    <div className="card-title">{t("Available Fields")}</div>
                    <div className="search-fields-row">
                      <div className="search-wrap">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                          type="text"
                          className="form-control"
                          value={fieldNameFilter}
                          id="fieldNameFilter"
                          placeholder={t("Search field name…")}
                          onChange={(e) => setFieldNameFilter(e.target.value)}
                        />
                      </div>
                      <div className="search-wrap">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                          type="text"
                          className="form-control"
                          value={labelNameFilter}
                          id="labelNameFilter"
                          placeholder={t("Search label…")}
                          onChange={(e) => setLableNameFilter(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="table-scroll">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th className="check-cell">
                            <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                          </th>
                          <th>{t("Field Name")}</th>
                          <th>{t("Field Label")}</th>
                          <th className="check-cell">{t("Required")}</th>
                          <th className="check-cell">{t("Read Only")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(filteredFields).map(([name, options]) => (
                          <tr
                            key={name}
                            className={
                              options.readonly && !["view"].includes(selectedMethod) ? "row-disabled" : ""
                            }>
                            <td className="check-cell">
                              {!(options.readonly && !["view"].includes(selectedMethod)) ? (
                                <input
                                  type="checkbox"
                                  value={name}
                                  checked={selectedFields.includes(name)}
                                  disabled={options.required}
                                  onChange={(e) => toggleSelectedFields(e.target.value, options.type)}
                                />
                              ) : null}
                            </td>
                            <td>
                              <span className="field-code" title={options.type}>
                                {name}
                              </span>
                            </td>
                            <td>
                              <span className="field-label-text">{options.string}</span>
                            </td>
                            <td className="check-cell">{renderReqBadge(options.required)}</td>
                            <td className="check-cell">{renderRoBadge(options.readonly)}</td>
                          </tr>
                        ))}
                        {Object.keys(fields).length === 0 ? (
                          <tr>
                            <td colSpan={5} className="fields-empty">
                              {t("Loading fields...")}
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="dashboard-empty">{t("Select")}</div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ApiGeneration;
